import type { Company } from 'common/api/endpoints/companies';
import type { Onboarding, OnboardingUpdateData } from 'common/api/endpoints/onboarding';
import type { LucideProps } from 'lucide-react';

export enum OnboardingSteps {
  setupGroups = 'setup-groups',
  createFirstIdeas = 'create-first-ideas',
  installFeedbackSources = 'install-feedback-sources',
  importFeedback = 'import-feedback',
  installIntegrations = 'install-integrations',
  enableFeedbackPortal = 'enable-feedback-portal',
  inviteTeam = 'invite-team',
  publicReviews = 'public-reviews',
  createBoard = 'create-board',
  customizePortal = 'customize-portal',
  widget = 'widget',
  syncUserData = 'sync-user-data',
  setupChangelogLabels = 'setup-changelog-labels',
  publishFirstChangelogEntry = 'publish-first-changelog-entry',
}

export enum OnboardingProducts {
  announcements = 'announcements',
  autopilot = 'autopilot',
  feedbackPortal = 'feedback-portal',
  ideas = 'ideas',
}

export type OnboardingValidator = (company: Company, onboarding: Onboarding) => boolean;

export enum OnboardingStepVisibilityBehavior {
  always = 'always',
  overflow = 'overflow',
  conditional = 'conditional',
}

export enum OnboardingStepType {
  button = 'button',
  expandable = 'expandable',
}

export enum OnboardingStepStatus {
  completed = 'completed',
  incomplete = 'incomplete',
}

export type BaseOnboardingStep = {
  condition?: OnboardingValidator;
  description: string;
  isDisabled?: OnboardingValidator;
  href: string | ((onboarding: Onboarding) => string);
  icon?: React.ComponentType<LucideProps>;
  id: OnboardingSteps;
  isCompleted: OnboardingValidator;
  overflowDescription?: string;
  overflowTitle?: string;
  title: string;
  type: OnboardingStepType;
  status: OnboardingStepStatus;
};

type OnboardingStepButton = BaseOnboardingStep & {
  type: OnboardingStepType.button;
  useCaseUpdatePayload?: Partial<OnboardingUpdateData>;
  buttonTitle: string;
  buttonVisible?: OnboardingValidator;
};

type OnboardingStepExpandable = BaseOnboardingStep & {
  type: OnboardingStepType.expandable;
  renderContent: () => React.ReactNode;
};

export type OnboardingStep = OnboardingStepButton | OnboardingStepExpandable;

export type OnboardingStepIdWithVisibleBehavior = {
  id: OnboardingSteps;
  visibilityBehavior: OnboardingStepVisibilityBehavior;
};

export type OnboardingProduct = {
  condition?: OnboardingValidator;
  description: string;
  id: OnboardingProducts;
  isDisabled?: OnboardingValidator;
  name: string;
  useCaseSetupPayload: Partial<OnboardingUpdateData>;
  steps: OnboardingStepIdWithVisibleBehavior[];
};
